import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  Checkbox,
  Input,
  Popover,
  PopoverAnchor,
  PopoverContent,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@invent/wl-ui-kit-next/ui';
import { X } from 'lucide-react';
import * as React from 'react';

import { FilterChip, useCondensed } from '@/design';
import { formatDollars } from '../../components/sec-adv-values';
import { FunnelHeader, ParamFunnel, SortHeader } from '../compliance/queue-headers';
import type { IListField } from './list-fields';
import { compactDollars, parseAmount, secItem } from './register';

/**
 * The firm list's own filter controls (SR-423): the toolbar's quick filters, the column headers —
 * the label sorts, the funnel filters, both over the page's URL keys (the work pages' work-item
 * family, `compliance/queue-headers.tsx`) — and the filter line's chips.
 */

/** A group of boxes — a quick filter's body. */
export function CheckList({
  label,
  options,
  chosen,
  onChange
}: {
  label: string;
  options: readonly { value: string; label: string }[];
  chosen: string[];
  onChange: (values: string[]) => void;
}) {
  return (
    <fieldset aria-label={label} className="grid max-h-80 gap-2.5 overflow-y-auto">
      {options.map((option) => (
        <label key={option.value} className="flex items-center gap-2">
          <Checkbox
            checked={chosen.includes(option.value)}
            onCheckedChange={(next) =>
              onChange(
                next === true
                  ? [...chosen, option.value]
                  : chosen.filter((value) => value !== option.value)
              )
            }
          />
          {option.label}
        </label>
      ))}
    </fieldset>
  );
}

/** A chip's summary of several values: the first, and how many more. */
export function summaryOf(values: string[]): string {
  if (values.length <= 1) return values[0] ?? '';
  return `${values[0]} +${values.length - 1}`;
}

/** An amount as its box shows it again: `$30B` where that is exact, else in full. */
function amountText(amount: Nullable<number>): string {
  if (amount === null) return '';
  const compact = compactDollars(amount);
  return parseAmount(compact) === amount ? compact : formatDollars(amount);
}

/** Total AUM's range as a chip says it, or nothing while it is open both ways. */
export function aumSummary(min: Nullable<number>, max: Nullable<number>): Nullable<string> {
  if (min === null && max === null) return null;
  if (min !== null && max !== null) return `${compactDollars(min)} – ${compactDollars(max)}`;
  return min !== null ? `≥ ${compactDollars(min)}` : `≤ ${compactDollars(max ?? 0)}`;
}

/** Total AUM between two amounts, either open — each box takes `30B`, `500M` or the full figure. */
export function AumChip({
  min,
  max,
  onApply
}: {
  min: Nullable<number>;
  max: Nullable<number>;
  onApply: (min: Nullable<number>, max: Nullable<number>) => void;
}) {
  const [low, setLow] = React.useState(amountText(min));
  const [high, setHigh] = React.useState(amountText(max));
  React.useEffect(() => setLow(amountText(min)), [min]);
  React.useEffect(() => setHigh(amountText(max)), [max]);
  const lowValue = parseAmount(low);
  const highValue = parseAmount(high);
  const unreadable =
    (low.trim() !== '' && lowValue === null) || (high.trim() !== '' && highValue === null);
  return (
    <FilterChip label="Total AUM" summary={aumSummary(min, max)}>
      <form
        className="grid gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (!unreadable) onApply(lowValue, highValue);
        }}
      >
        <div className="flex items-center gap-2">
          <Input
            aria-label="Total AUM at least"
            placeholder="Min, e.g. 500M"
            value={low}
            onChange={(event) => setLow(event.target.value)}
            className="h-8 w-36"
          />
          <span className="text-muted-foreground">–</span>
          <Input
            aria-label="Total AUM at most"
            placeholder="Max, e.g. 30B"
            value={high}
            onChange={(event) => setHigh(event.target.value)}
            className="h-8 w-36"
          />
        </div>
        {unreadable && (
          <p role="alert" className="text-xs text-destructive">
            Write an amount as 30B, 30.3B, 500M or 30,000,000,000.
          </p>
        )}
        <div className="flex justify-end gap-2">
          {(min !== null || max !== null) && (
            <Button type="button" variant="ghost" size="sm" onClick={() => onApply(null, null)}>
              Clear
            </Button>
          )}
          <Button type="submit" size="sm" disabled={unreadable}>
            Apply
          </Button>
        </div>
      </form>
    </FilterChip>
  );
}

/** The order a column's first press sorts by: an amount, a count or a day largest first. */
export function firstDir(field: IListField): 'asc' | 'desc' {
  return field.kind === 'money' || field.kind === 'number' || field.kind === 'date' ? 'desc' : 'asc';
}

function orderWords(field: IListField, dir: 'asc' | 'desc'): string {
  if (field.kind === 'date') return dir === 'desc' ? 'newest first' : 'oldest first';
  if (field.kind === 'money' || field.kind === 'number') {
    return dir === 'desc' ? 'largest first' : 'smallest first';
  }
  return dir === 'asc' ? 'A to Z' : 'Z to A';
}

/** A column's funnel, held by the page so a filter chip's press opens it. */
export interface IColumnFunnel {
  code: string;
  said: Nullable<string>;
  body: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * A firm column's header: the label's press sorts by it — again, the other way — its tooltip the
 * Form ADV item and the order; the funnel after it where the column filters. A column survey-api
 * does not sort is a plain label.
 */
export function FirmHeader({
  field,
  sorted,
  onSort,
  funnel
}: {
  field: IListField;
  sorted: Nullable<'asc' | 'desc'>;
  onSort: () => void;
  funnel?: IColumnFunnel;
}) {
  const funnelNode = funnel && (
    <ParamFunnel
      code={funnel.code}
      label={field.label}
      said={funnel.said}
      open={funnel.open}
      onOpenChange={funnel.onOpenChange}
    >
      {funnel.body}
    </ParamFunnel>
  );
  if (!field.sortable) {
    if (funnelNode) return <FunnelHeader label={field.label} funnel={funnelNode} />;
    if (!field.item) return <>{field.label}</>;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="decoration-dotted underline-offset-4 hover:underline">{field.label}</span>
        </TooltipTrigger>
        <TooltipContent>Form ADV Item {secItem(field.item)}</TooltipContent>
      </Tooltip>
    );
  }
  const dir = sorted ?? firstDir(field);
  const item = field.item ? `Form ADV Item ${secItem(field.item)} · ` : '';
  return (
    <SortHeader
      label={field.label}
      direction={dir}
      active={sorted !== null}
      note={`${item}${sorted ? `Sorted ${orderWords(field, dir)}` : `Sort ${orderWords(field, dir)}`}`}
      onPress={onSort}
      funnel={funnelNode}
    />
  );
}

/**
 * A column filter in the filter line: its words, whose press opens the column's funnel — or, the
 * column not drawn, the same values beside the chip — and ✕, which drops it.
 */
export function ParamChip({
  words,
  body,
  onPress,
  onRemove
}: {
  words: string;
  /** The funnel's values, for a column not drawn. */
  body: React.ReactNode;
  /** Opens the column's funnel; false where the column is not drawn. */
  onPress: () => boolean;
  onRemove: () => void;
}) {
  const [open, setOpen] = React.useState(false);
  // In the compact bar the line is narrow: a chip gives way to its ✕, its words cut short. Its cap
  // is a length, never a share of its box: a row of applied filters sizes each chip's box by the
  // chip, and a share there left the box as wide as the words uncut, a gap after the chip.
  const { condensed } = useCondensed();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <span
          className={cn(
            'inline-flex h-7 max-w-96 items-center rounded-full border border-border bg-[var(--card)] text-[0.8125rem]',
            condensed ? 'min-w-0' : 'shrink-0'
          )}
        >
          <button
            type="button"
            onClick={() => {
              if (!onPress()) setOpen(true);
            }}
            className="min-w-0 truncate rounded-l-full py-0.5 pr-1 pl-2.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {words}
          </button>
          <button
            type="button"
            aria-label={`Remove the filter: ${words}`}
            onClick={onRemove}
            className="inline-flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="size-3.5" aria-hidden />
          </button>
        </span>
      </PopoverAnchor>
      <PopoverContent align="start" aria-label={words} className="grid w-72 gap-2 p-3 text-[0.8125rem]">
        {body}
      </PopoverContent>
    </Popover>
  );
}
